import React, { useCallback, useMemo } from 'react';
import { View, StyleSheet, Switch } from 'react-native';
import { Text } from '@/components/common/text';
import { PropertyOrgSelectors } from './PropertyOrgSelectors';
import { useAdminNavigation } from '@/hooks/admin/useAdminNavigation';
import { useDisplaySelectors } from '@/hooks/admin/useDisplaySelectors';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  setSelectedOrganizationId,
  setSelectedPropertyId,
  setIsMenuOpen,
  setExpandedMenuIds,
} from '@/store/slices/admin/adminUISlice';
import { selectIsMenuOpen } from '@/store/selectors/adminUISelectors';
import {
  ADMIN_NAVIGATION_ITEMS,
  ADMIN_NAV_GROUP_LABELS,
  ADMIN_NAV_PARENT_ICON_KEYS,
  ADMIN_NAV_SUB_ICON_KEYS,
  FUNDING_PATH,
} from '@/constants/admin/adminNavigation';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import {
  ISharedNavigationItem,
  ISharedNavigationGroupConfig,
  SvgComponent,
} from '@/constants/shared/navigation';
import { SVG_COMPONENTS } from '@/assets/admin';
import { MobileHeaderWithGlassMenu } from '@/components/shared/redesign';

const getMenuIdForPath = (path: string): string | null => {
  for (const item of ADMIN_NAVIGATION_ITEMS) {
    if (item.path === path) return item.id;
    if (item.subNavItems?.some((sub) => sub.path === path)) return item.id;
  }
  return null;
};

interface AdminHeaderWithMenuProps {
  onLogoPress?: () => void;
  onSignOut?: () => void;
  onNavigate?: (path: string) => void;
  currentPath?: string;
}

export const AdminHeaderWithMenu: React.FC<AdminHeaderWithMenuProps> = React.memo(({
  onLogoPress,
  onSignOut,
  onNavigate,
  currentPath = '/properties',
}) => {
  const dispatch = useAppDispatch();
  const isMenuOpen = useAppSelector(selectIsMenuOpen);

  const { mode, toggleTheme, colors, components } = useThemeColors();
  const isDarkMode = mode === 'dark';

  const { navigationItems, adminUser, getFilteredSubNavItems } = useAdminNavigation();
  const displaySelectors = useDisplaySelectors(currentPath);
  const isFunding = useMemo(() => currentPath.includes(FUNDING_PATH), [currentPath]);

  // Flatten admin parent → subNavItems into a single grouped list, with parent.id as the group key.
  const navItems = useMemo<ISharedNavigationItem[]>(() => {
    const items: ISharedNavigationItem[] = [];
    for (const parent of navigationItems) {
      const subItems = getFilteredSubNavItems(parent.subNavItems) ?? [];
      for (const sub of subItems) {
        const iconKey = ADMIN_NAV_SUB_ICON_KEYS[sub.path];
        items.push({
          id: `${parent.id}:${sub.path}`,
          title: sub.title,
          path: sub.path,
          group: parent.id,
          icon: iconKey ? (SVG_COMPONENTS[iconKey] as SvgComponent) : null,
        });
      }
    }
    return items;
  }, [navigationItems, getFilteredSubNavItems]);

  const navGroupConfig = useMemo<ISharedNavigationGroupConfig>(
    () => {
      const icons: Record<string, SvgComponent> = {};
      for (const id of Object.keys(ADMIN_NAV_PARENT_ICON_KEYS)) {
        const key = ADMIN_NAV_PARENT_ICON_KEYS[id];
        icons[id] = SVG_COMPONENTS[key] as SvgComponent;
      }
      return {
        order: navigationItems.map((item) => item.id),
        labels: ADMIN_NAV_GROUP_LABELS,
        icons,
      };
    },
    [navigationItems],
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      dispatch(setIsMenuOpen(open));
    },
    [dispatch],
  );

  const handleNavigate = useCallback(
    (path: string) => {
      const activeMenuId = getMenuIdForPath(path);
      dispatch(setExpandedMenuIds(activeMenuId ? [activeMenuId] : []));
      onNavigate?.(path);
    },
    [dispatch, onNavigate],
  );

  const handleOrganizationChange = useCallback(
    (orgId: number) => {
      dispatch(setSelectedOrganizationId(orgId));
      const availableProperty = adminUser.properties?.find(
        (prop) => prop.organizationId === orgId,
      );
      if (availableProperty) {
        dispatch(setSelectedPropertyId(availableProperty.id));
      }
    },
    [dispatch, adminUser.properties],
  );

  const handlePropertyChange = useCallback(
    (propId: number) => {
      dispatch(setSelectedPropertyId(propId));
    },
    [dispatch],
  );

  const topMenuSlot = (
    <View style={styles.topMenuSlot}>
      <PropertyOrgSelectors
        organizations={adminUser.organizations || []}
        properties={adminUser.properties || []}
        selectedOrganizationId={adminUser.selectedOrganizationId}
        selectedPropertyId={adminUser.selectedPropertyId}
        onOrganizationChange={handleOrganizationChange}
        onPropertyChange={handlePropertyChange}
        showPropertySelector={!isFunding && displaySelectors.includes('property')}
        disableOrganization={!displaySelectors.includes('organization')}
        disabled={false}
      />
    </View>
  );

  const bottomSlot = (
    <View style={styles.themeToggleRow}>
      <Text style={[styles.themeToggleLabel, { color: colors.text.secondary }]}>
        Dark mode
      </Text>
      <Switch
        value={isDarkMode}
        onValueChange={toggleTheme}
        trackColor={{ false: colors.separator, true: PRIMITIVE_COLORS.resioBlue }}
        thumbColor={components.toggle.thumb}
        accessibilityLabel="Toggle dark mode"
      />
    </View>
  );

  return (
    <MobileHeaderWithGlassMenu
      currentPath={currentPath}
      onNavigate={handleNavigate}
      onLogoPress={onLogoPress}
      onSignOut={onSignOut}
      navItems={navItems}
      navGroupConfig={navGroupConfig}
      topMenuSlot={topMenuSlot}
      bottomSlot={bottomSlot}
      isOpen={isMenuOpen}
      onOpenChange={handleOpenChange}
      variant="gradient"
    />
  );
});
AdminHeaderWithMenu.displayName = 'AdminHeaderWithMenu';

const styles = StyleSheet.create({
  topMenuSlot: {
    width: '100%',
    paddingVertical: 4,
    overflow: 'visible',
  },
  themeToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 0,
    paddingBottom: 0,
  },
  themeToggleLabel: {
    fontSize: 16,
    fontWeight: '600',
  },
});
